import { AppraisalQuestion } from '../useAppraisalFlow'
import useTranslation from 'next-translate/useTranslation'
import { Button } from '@/components/Button'
import { EAppraisalQuestionInputType } from '@/types/gql/graphql'
import FlowText from './FlowText'
import NumberInput from './NumberInput'
import OptionGrid from './OptionGrid'

const amountFormatter = new Intl.NumberFormat('de-AT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

export interface ResultViewProps {
  amountEur: number
  /** How the estimate was made and what to do next (AI-estimated luxury items). */
  disclaimer?: string | null
  /** Editable inputs the result was computed from (e.g. gold weight/fineness). */
  adjustQuestions: AppraisalQuestion[]
  answers: Record<string, string>
  canRecalculate: boolean
  onAnswerChange: (key: string, value: string | null) => void
  onRecalculate: () => void
  onCtaClick?: () => void
  isSubmitting?: boolean
}

export default function ResultView({
  amountEur,
  disclaimer,
  adjustQuestions,
  answers,
  canRecalculate,
  onAnswerChange,
  onRecalculate,
  onCtaClick,
  isSubmitting,
}: ResultViewProps) {
  const { t } = useTranslation('wertCheck')

  const isDirty = adjustQuestions.some(
    (question) => answers[question.key] !== (question.prefill ?? undefined),
  )

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-static-white rounded-xl shadow-[0_0_2rem_rgba(0,0,0,0.1)] p-6 flex flex-col items-center gap-4 text-center">
        <h2 className="text-heading-2xs text-primary uppercase tracking-wide">
          {t('result_title')}
        </h2>

        <p className="text-heading-m text-brand-secondary font-bold">
          {amountFormatter.format(amountEur)}
        </p>

        {disclaimer && (
          <FlowText
            text={disclaimer}
            className="text-m text-primary"
            data-testid="result-disclaimer"
          />
        )}

        {onCtaClick && (
          <Button
            type="button"
            variant="cta"
            className="w-full"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={onCtaClick}
          >
            {t('result_cta')}
          </Button>
        )}
      </div>

      {adjustQuestions.length > 0 && (
        <div className="bg-static-white rounded-xl shadow-[0_0_2rem_rgba(0,0,0,0.1)] p-4 flex flex-col gap-4">
          <h3 className="text-heading-2xs text-primary text-center uppercase tracking-wide">
            {t('adjust_title')}
          </h3>

          {adjustQuestions.map((question) => (
            <div key={question.key} className="flex flex-col gap-2">
              <p className="text-m text-secondary">{question.title}</p>

              {question.inputType === EAppraisalQuestionInputType.Number ? (
                <NumberInput
                  value={answers[question.key] ?? ''}
                  unit={question.unit}
                  onChange={(value) =>
                    onAnswerChange(question.key, value === '' ? null : value)
                  }
                />
              ) : (
                <OptionGrid
                  question={question}
                  value={answers[question.key]}
                  onChange={(value) => onAnswerChange(question.key, value)}
                />
              )}
            </div>
          ))}

          <Button
            type="button"
            variant="secondary"
            className="w-full"
            disabled={!isDirty || !canRecalculate}
            onClick={onRecalculate}
          >
            {t('recalculate')}
          </Button>
        </div>
      )}
    </div>
  )
}
